// Per-dialect computed / generated-column synthesizer (v2 Slice A3).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice A3.
//
// Each engine spells the GENERATED clause differently:
//   - mssql:  `AS <expr> [PERSISTED]`            — no STORED/VIRTUAL keyword.
//   - pgsql:  `GENERATED ALWAYS AS (expr) STORED`
//             — only STORED is supported (12+); virtual generated columns
//             are rejected.
//   - mysql:  `GENERATED ALWAYS AS (expr) [VIRTUAL | STORED]`
//             — VIRTUAL is the default but we always emit it explicitly to
//             keep the round-trip lossless.
//   - oracle: `GENERATED ALWAYS AS (expr) [VIRTUAL]`
//             — STORED is not valid syntax for column-level GENERATED in
//             Oracle (12c+); passing `virtual: false` emits the bare clause.
//   - sqlite: `GENERATED ALWAYS AS (expr) [VIRTUAL | STORED]` (3.31.0+).
//
// `expr` is opaque text — the synthesizer wraps it in parentheses (every
// engine requires the parens) but does not re-parse it.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { GeneratedSpec } from '../../types';
import { DdlEditError } from '../../types';

export function formatGeneratedSpec(engine: SqlEngine, gen: GeneratedSpec): string {
  if (engine === 'mssql') return formatMssql(gen);
  if (engine === 'postgres') return formatPgsql(gen);
  if (engine === 'mysql') return formatMysql(gen);
  if (engine === 'oracle') return formatOracle(gen);
  return formatSqlite(gen);
}

function formatMssql(gen: GeneratedSpec): string {
  // mssql: `AS (expr) [PERSISTED]` — no GENERATED ALWAYS keyword, no STORED.
  // virtual === true is the default (computed on read, not persisted); we
  // omit PERSISTED in that case. virtual === false / undefined → PERSISTED.
  const persisted = gen.virtual === true ? '' : ' PERSISTED';
  return `AS (${gen.expr})${persisted}`;
}

function formatPgsql(gen: GeneratedSpec): string {
  if (gen.virtual === true) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: pgsql does not support virtual generated columns — `
        + `only STORED is valid (drop 'virtual: true' from GeneratedSpec)`,
    );
  }
  return `GENERATED ALWAYS AS (${gen.expr}) STORED`;
}

function formatMysql(gen: GeneratedSpec): string {
  const persistence = gen.virtual === true ? 'VIRTUAL' : 'STORED';
  return `GENERATED ALWAYS AS (${gen.expr}) ${persistence}`;
}

function formatOracle(gen: GeneratedSpec): string {
  // Oracle column-level GENERATED supports VIRTUAL only — there is no
  // column-level STORED keyword (computed columns are virtual unless the
  // table is set up for materialized expressions). When virtual === false /
  // undefined we still emit the bare clause; Oracle treats it as virtual.
  const suffix = gen.virtual === true ? ' VIRTUAL' : '';
  return `GENERATED ALWAYS AS (${gen.expr})${suffix}`;
}

function formatSqlite(gen: GeneratedSpec): string {
  const persistence = gen.virtual === true ? 'VIRTUAL' : 'STORED';
  return `GENERATED ALWAYS AS (${gen.expr}) ${persistence}`;
}
